import axios from 'axios';
import authService from '../services/auth';
import loggerService from '../services/logger';
import store from '../store/store';
import { getAuthMode, AUTH_MODE } from '../utils/token';

// CRITICAL: Enable credentials globally for httpOnly cookies
// This allows cookies to be sent automatically with every request
axios.defaults.withCredentials = true;
import { sanitizeError } from './error-sanitizeError';

export default (() => {
  const networkAssetURL = process.env.VUE_APP_NETWORK_ASSETS_URL || '';
  const clientEdgeURL = process.env.VUE_APP_CLIENT_EDGE_BASE || '';
  const accessURL = process.env.VUE_APP_ACCESS_SERVER || '';

  const axiosInstance = axios.create();
  injectAuthIntoAxios(axiosInstance);
  injectLoggerIntoAxios(axiosInstance);

  function getNetworkAsset(hideLoadingIndicator) {
    const axiosInstanceNetworkAsset = axios.create({ baseURL: networkAssetURL });    
    injectAuthIntoAxios(axiosInstanceNetworkAsset);
    if (hideLoadingIndicator !== 'hideLoadingIndicator') {
      injectLoadingIntoAxios(axiosInstanceNetworkAsset);
    }
    injectLoggerIntoAxios(axiosInstanceNetworkAsset);
    return axiosInstanceNetworkAsset;
  }

  function getClientEdge(hideLoadingIndicator) {
    const axiosInstanceClientEdge = axios.create({ baseURL: clientEdgeURL });
    injectAuthIntoAxios(axiosInstanceClientEdge);
    injectLoggerIntoAxios(axiosInstanceClientEdge);
    if (hideLoadingIndicator !== 'hideLoadingIndicator') {
      injectLoadingIntoAxios(axiosInstanceClientEdge);
    }
    return axiosInstanceClientEdge;
  }

  function getAccess(hideLoadingIndicator) {
    const axiosInstance = axios.create({ baseURL: accessURL });
    injectAuthIntoAxios(axiosInstance);
    injectLoggerIntoAxios(axiosInstance);
    if (hideLoadingIndicator !== 'hideLoadingIndicator') {
      injectLoadingIntoAxios(axiosInstance);
    }
    return axiosInstance;
  }

  /**
   * Analyzes refresh token errors to determine if user should be logged out
   * 
   * HOTFIX: Always logout when refresh fails to prevent "limbo" state
   * If refresh fails for ANY reason (401, 500, network error, etc.),
   * the user cannot continue using the app, so we must force logout.
   * 
   * ZOMBIE TOKEN FIX: Detects invalid_token errors and calls cleanup endpoint
   * to remove zombie cookies (cookies that exist but tokens don't exist in DB)
   */
  function analyzeRefreshError(error) {
    const status = error.response?.status;
    const errorData = error.response?.data;
    
    console.log('🔍 [AUTH] Analyzing refresh error - FULL DETAILS:', {
      status,
      statusText: error.response?.statusText,
      message: error.message,
      hasResponse: !!error.response,
      isNetworkError: !error.response,
      isTimeout: error.code === 'ECONNABORTED',
      errorCode: error.code,
      responseData: errorData,
      responseHeaders: error.response?.headers,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      timestamp: new Date().toISOString()
    });
    
    // Detect "zombie token" - cookies present but token invalid in backend
    // This happens when OAuth2 server restarts or DB cleanup removes tokens
    const isInvalidToken = errorData?.error === 'invalid_token' || 
                           (status === 401 && errorData?.error_description?.includes('invalid'));
    
    if (isInvalidToken) {
      console.warn('⚠️ [AUTH] ZOMBIE TOKEN DETECTED - Cookies exist but token invalid in backend');
      console.warn('⚠️ [AUTH] Calling cleanup endpoint to clear zombie cookies...');
      
      // Call cleanup endpoint (async, non-blocking)
      // This removes the zombie cookies so re-login will work cleanly
      const cleanupUrl = accessURL + '/access/sso/clear-cookies-client';
      axios.post(cleanupUrl, {}, { withCredentials: true })
        .then(() => {
          console.log('✅ [AUTH] Zombie cookies cleared successfully');
        })
        .catch(cleanupError => {
          console.error('❌ [AUTH] Failed to clear zombie cookies:', cleanupError.message);
          // Not critical if it fails - logout will clear frontend state
          // and next login will clean cookies anyway
        });
    }
    
    // ALWAYS logout when refresh fails
    // There is no scenario where the user can continue without a valid token
    console.error('❌ [AUTH] LOGOUT DECISION: Refresh failed - forcing logout to prevent limbo state');
    console.error('❌ [AUTH] LOGOUT REASON:', {
      primaryReason: status === 401 ? 'Token expired/invalid' : 
                     status === 403 ? 'Forbidden' :
                     status >= 500 ? 'Server error' :
                     !error.response ? 'Network error' : 'Unknown error',
      isZombieToken: isInvalidToken,
      willLogout: true
    });
    return true;
  }

  function injectAuthIntoAxios(axiosObj) {
    axiosObj.interceptors.request.use(
      // Intercept API requests and add authentication
      async function (config) {
        const mode = getAuthMode();
        
        // Ensure withCredentials is set for this request
        config.withCredentials = true;
        
        if (mode === AUTH_MODE.COOKIES) {
          // COOKIES MODE: Don't add Authorization header
          // Backend HybridAuthFilter will read httpOnly cookies and add the header automatically
          return config;
        } else {
          // LOCALSTORAGE MODE: Add Authorization header manually
          const accessToken = await authService.getAccessToken();
          if (accessToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
          }
          return config;
        }
      },
      function (error) {
        return Promise.reject(error)
      }
    );

      axiosObj.interceptors.response.use(
      function (response) {
        return response;
      },
      async function (error) {
        const originalRequest = error.config;
        
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          console.log('🔄 [AUTH] 401 DETECTED - Starting refresh flow:', {
            url: originalRequest.url,
            method: originalRequest.method,
            authMode: getAuthMode(),
            hasAuthHeader: !!originalRequest.headers?.Authorization,
            timestamp: new Date().toISOString()
          });
          
          console.log('🔄 [AUTH] Original 401 response details:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
            headers: error.response.headers,
            wwwAuthenticate: error.response.headers?.['www-authenticate']
          });
          
          try {
            console.log('🔄 [AUTH] Attempting token refresh...');
            const refreshResult = await authService.refreshToken();
            
            console.log('✅ [AUTH] Refresh successful - Details:', {
              hasAccessToken: !!refreshResult?.access_token,
              expiresIn: refreshResult?.expires_in,
              tokenType: refreshResult?.token_type
            });
            
            console.log('🔄 [AUTH] Retrying original request:', {
              url: originalRequest.url,
              method: originalRequest.method
            });
            
            const retryResult = await axiosObj(originalRequest);
            
            console.log('✅ [AUTH] Retry successful:', {
              status: retryResult.status,
              url: originalRequest.url
            });
            
            return retryResult;
            
          } catch (refreshError) {
            console.error('❌ [AUTH] Refresh failed - Complete error info:', {
              error: refreshError.message,
              status: refreshError.response?.status,
              statusText: refreshError.response?.statusText,
              data: refreshError.response?.data,
              originalUrl: originalRequest.url,
              originalMethod: originalRequest.method
            });
            
            const shouldLogout = analyzeRefreshError(refreshError);
            
            if (shouldLogout) {
              console.error('🚪 [AUTH] EXECUTING LOGOUT - Dispatching logout action');
              console.error('🚪 [AUTH] User will be redirected to login page');
              store.dispatch('logout', {vuetify: null});
            } else {
              console.warn('⚠️ [AUTH] NOT LOGGING OUT - Keeping user logged in (error may be temporary)');
            }
            
            throw refreshError;
          }
        }
        
        throw error;
      }
    );
  }

  function injectLoggerIntoAxios(axiosObj) {
    axiosObj.interceptors.response.use(
      function (response) {
        // Any status code that lie within the range of 2xx cause this function to trigger
        // Returning response is requirement of axios interceptors
        return response;
      },
      function (error) {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        // Encapsulating logging code in try catch to prevent cyclic error logging if error logging fails
        try {
          const response = error && error.response;
          // Log all errors which are not 401 errors because logging API is authenticated
          if (response && response.status !== 401) {
            let url = response.config.url;
            if (response.config.baseURL) {
              url = `${response.config.baseURL}${url}`;
            }
            loggerService.error({
              message: 'API error',
              status: response.status,
              source: 'Axios Response Interceptor',
              url,
            }).catch(() => {});
          }
        } catch (e) { e; }
        // Returning a rejected Promise of error is requirement of axios interceptors
        const sanitizedError = sanitizeError(error.response ? error.response : error);
        return Promise.reject(sanitizedError);
      }
    );
  }

  function injectLoadingIntoAxios(axiosObj) {        
    // for each request / response / error increase or decrease the amount of loading items in store
    axiosObj.interceptors.request.use(
      function (config) {
        store.dispatch('layout/setLoading', true);
        return config;
      }
    );
    axiosObj.interceptors.response.use(
      function (response) {
        store.dispatch('layout/setLoading', false);
        return response;
      },
      function (error) {
        store.dispatch('layout/setLoading', false);
        return Promise.reject(error.response ? error.response : error);
      }
    );
  }

  return {
    axiosInstance, 
    getNetworkAsset,
    getClientEdge,
    getAccess,
  };
})();
